import { useEffect, useRef, type HTMLAttributes, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { Bento } from "@/components/ui/box";
import type { Box, FactBox, ListBox, Read } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TileTable } from "@/components/ui/tile-table";
import { gradebookApi, type MyGpa, type MyGpaTerm, type MyGradesCourse } from "@/lib/api/gradebook";
import { courseHomeApi } from "@/lib/api/courseHome";
import { outcomesApi, type StudentOutcome } from "@/lib/api/outcomes";
import { courseWorkspacePath } from "@/navigation/routes";
import { registerRead,
  registerText,
  registerPhrases,
} from "@/lib/register-copy";
import { myRegradesList } from "@/components/regrades/regrade-request-boxes";
import { regradeKeys, regradesApi } from "@/lib/api/regrades";

export function GradesPage({ courseId, embedded = false }: { courseId?: string; embedded?: boolean } = {}) {
  const gradesQuery = useQuery({ queryKey: ["me", "grades"], queryFn: () => gradebookApi.getMyGrades() });
  const gpaQuery = useQuery({ queryKey: ["me", "gpa"], queryFn: () => gradebookApi.getMyGpa(), enabled: !embedded });
  const outcomesQuery = useQuery({ queryKey: ["outcomes", "me"], queryFn: () => outcomesApi.getMine() });
  // S6 (#1293): own regrade requests stay readable after the course or enrollment ends.
  const regradesQuery = useQuery({ queryKey: regradeKeys.mine, queryFn: regradesApi.mine, retry: false });
  const regrades = myRegradesList(regradesQuery, courseId);
  const workingCourses = new Map((gradesQuery.data?.courses ?? []).map((course) => [course.course_id, course]));
  const standing: FactBox<MyGpa> = {
    kind: "fact", id: "standing", job: registerText.readYourCertifiedStanding, label: registerText.standing, owner: { system: "imam" }, fold: "never",
    read: registerRead(gpaQuery, registerText.certifiedStanding), detail: () => registerText.onlyCertifiedOutcomesContributeToGPA,
    prose: (value) => value.cumulative === null ? registerText.noCertifiedGPAYet : registerPhrases.cumulativeGpa(value.cumulative),
  };
  type CourseResult = MyGradesCourse & { outcome?: StudentOutcome };
  const courseRows = new Map<string, CourseResult>();
  for (const course of workingCourses.values()) courseRows.set(course.course_id, course);
  for (const outcome of outcomesQuery.data?.outcomes ?? []) courseRows.set(outcome.course_id, { ...outcome, outcome });
  const visible = [...courseRows.values()].filter((row) => !courseId || row.course_id === courseId);
  const refreshRecords = () => { void gradesQuery.refetch(); void outcomesQuery.refetch(); };
  const resultRead: Read<CourseResult[]> = !visible.length && (gradesQuery.isError || outcomesQuery.isError)
      ? { status: "failed", sentence: registerText.courseResultsAreUnavailable, retry: refreshRecords }
      : gradesQuery.data || outcomesQuery.data
        ? { status: "ready", value: visible, readAt: new Date(Math.min(...[gradesQuery.dataUpdatedAt, outcomesQuery.dataUpdatedAt].filter(Boolean), Date.now())).toISOString() }
        : { status: "loading" };
  const results: ListBox<CourseResult[], CourseResult> = {
    kind: "list", id: "student-course-results", job: registerText.readYourCourseResults, label: registerText.courseResults, owner: { system: "imam" }, fold: "never",
    read: resultRead, detail: () => "",
    state: (rows) => ({ class: "quiet", word: rows.length ? registerText.yourAcademicRecord : registerText.noCourseRecordsYet }),
    openDetail: () => outcomesQuery.isError ? registerText.closeoutRecordsCouldNotBeRefreshedEachCourseKeepsIts : undefined,
    rows: (rows) => rows,
    row: {
      key: (row) => row.course_id,
      // Inside one course its band names it; the row names only the result.
      title: (row) => courseId ? registerText.yourResult : `${row.course_code} · ${row.course_title}`,
      state: (row) => {
        const status = row.outcome?.status;
        return { class: status === "certified" || status === "corrected" ? "done" : "quiet",
          word: status === "awaiting_certification" ? registerText.awaitingCertification2 : status === "provisional" ? registerText.provisional : status === "corrected" ? registerText.corrected : status === "certified" ? registerText.certified : outcomesQuery.isPending ? registerText.checkingResult : registerText.resultUnavailable };
      },
      startsOpen: (row) => row.course_id === courseId,
      facts: (row) => row.outcome?.standing_certification?.certified_letter ?? "", act: () => ({ does: "open", verb: registerText.readResult }),
      detail: (row) => {
        if (gradesQuery.isPending) return <WorkingAvailabilityRecord course={row} outcome={row.outcome} state="loading" />;
        if (gradesQuery.isError) return <WorkingAvailabilityRecord course={row} outcome={row.outcome} state="error" onRetry={() => void gradesQuery.refetch()} />;
        const course = workingCourses.get(row.course_id);
        return course ? <CourseBreakdown course={course} closeout={row.outcome} closeoutLoading={outcomesQuery.isPending} closeoutError={outcomesQuery.isError} retryCloseout={() => void outcomesQuery.refetch()} /> : row.outcome ? <CloseoutOnlyRecord outcome={row.outcome} /> : null;
      },
    },
  };
  const history: ListBox<MyGpa, MyGpaTerm> = {
    kind: "list", id: "student-grade-history", job: registerText.readCertifiedAndProvisionalTermHistory, label: registerText.gradeHistory, owner: { system: "imam" }, fold: "never",
    read: registerRead(gpaQuery, registerText.gradeHistory), detail: () => registerText.eachTermKeepsItsCertificationState,
    state: (value) => ({ class: "quiet", word: value.terms.length ? registerText.termsOnFile : registerText.noTermsOnFile }),
    rows: (value) => value.terms,
    row: { key: (term) => term.semester, title: (term) => term.semester,
      state: (term) => ({ class: term.provisional ? "quiet" : "done", word: term.provisional ? registerText.provisional : registerText.certified }),
      act: () => ({ does: "open", verb: registerText.readTerm }),
      detail: (term) => <ul className="grid gap-2">{term.courses.map((course) => <li key={course.code}>{course.code} · {course.credits} {registerText.credits}{course.letter ?? registerText.notCertified}</li>)}</ul> },
  };
  return <div className={embedded ? "min-w-0" : "app-page"}>
    {!embedded && <AcademicPageHeader eyebrow={registerText.studentAcademicRecord} title={registerText.grades} description={registerText.workingResultsPerCourseAndCertifiedTermHistory} />}
    {courseId && outcomesQuery.data && gradesQuery.data && !courseRows.has(courseId)
      ? <p>{registerText.thatCourseIsNotOnYourRecord}<Link data-nav to="/student/grades">{registerText.viewAllCourses}</Link></p>
      : <Bento id="student-grades" journey={`student-grades-${courseId ?? "all"}`} boxes={(embedded ? [results, ...(regrades ? [regrades] : [])] : [standing, results, ...(regrades ? [regrades] : []), history]) as Box[]} />}
    {courseId && <Link data-nav to="/student/grades">{registerText.viewAllCourses}</Link>}
  </div>;
}

/**
 * One course's record inside the results tile: its code as the eyebrow, its
 * title as the row heading, an optional lead (the figure and its chips), then
 * the body. Rows separate with the rule; the tile's own rule sits above the
 * first.
 */
function CourseRecord({
  code,
  title: _title,
  lead,
  children,
  ...rest
}: {
  code: string;
  title: string;
  lead?: ReactNode;
  children: ReactNode;
} & HTMLAttributes<HTMLElement>) {
  return <section className="grid min-w-0 gap-3" {...rest} aria-label={registerPhrases.workingResult(code)}>
    {lead}{children}
  </section>;
}

function WorkingAvailabilityRecord({
  outcome,
  course,
  state,
  onRetry,
}: {
  outcome?: StudentOutcome;
  course: MyGradesCourse;
  state: "loading" | "error";
  onRetry?: () => void;
}) {
  return (
    <CourseRecord code={course.course_code} title={course.course_title} aria-busy={state === "loading" || undefined}>
      <StudentCloseoutStatus
        outcome={outcome}
        isLoading={false}
        isError={false}
        onRetry={() => undefined}
      />
      {state === "loading" ? (
        <p className="text-ui text-muted-foreground" role="status">
          {registerText.checkingCurrentWorkingResultAvailability}</p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-ui text-muted-foreground">
            {registerText.workingResultAvailabilityCouldNotBeLoadedYourCourseCloseout}</p>
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>{registerText.retryWorkingResult}</Button>
        </div>
      )}
    </CourseRecord>
  );
}

function CloseoutOnlyRecord({ outcome }: { outcome: StudentOutcome }) {
  const history = useQuery({ queryKey: ["me", "course-record-access", outcome.course_id], queryFn: () => courseHomeApi.getLearningContext(outcome.course_id), retry: false });
  return (
    <CourseRecord code={outcome.course_code} title={outcome.course_title}>
      <StudentCloseoutStatus
        outcome={outcome}
        isLoading={false}
        isError={false}
        onRetry={() => undefined}
      />
      <p className="text-ui text-muted-foreground">
        {registerText.noCurrentWorkingResultIsAvailableForThisCourseCertified}</p>
      {history.data && !history.isError && <Link className="inline-flex min-h-11 items-center text-primary underline" to={`/student/courses/${outcome.course_id}`}>Read coursework and feedback</Link>}
    </CourseRecord>
  );
}

function CourseBreakdown({
  course,
  closeout,
  closeoutLoading,
  closeoutError,
  retryCloseout,
}: {
  course: MyGradesCourse;
  closeout?: StudentOutcome;
  closeoutLoading: boolean;
  closeoutError: boolean;
  retryCloseout: () => void;
}) {
  const knownBasisId = useRef<string | undefined>(undefined);
  const resultQuery = useQuery({
    queryKey: ["me", "provisional-result", course.course_id],
    queryFn: () => gradebookApi.getMyProvisionalResult(course.course_id, knownBasisId.current),
  });
  const read = resultQuery.data;

  useEffect(() => {
    if (read?.status === "current") {
      knownBasisId.current = read.basis_id;
    }
  }, [read]);

  const refreshResult = () => {
    if (read?.status !== "current") {
      knownBasisId.current = undefined;
    }
    void resultQuery.refetch();
  };

  if (resultQuery.isLoading) {
    return (
      <CourseRecord code={course.course_code} title={course.course_title} aria-busy="true">
        <StudentCloseoutStatus outcome={closeout} isLoading={closeoutLoading} isError={closeoutError} onRetry={retryCloseout} />
        <p className="text-ui text-muted-foreground">{registerText.checkingYourProvisionalResult}</p>
      </CourseRecord>
    );
  }

  if (resultQuery.isError || resultQuery.isPlaceholderData || resultQuery.isFetching || !read) {
    const supportUrl = read?.status !== "current" ? read?.support_url : undefined;
    return (
      <CourseRecord code={course.course_code} title={course.course_title} aria-live="polite">
        <StudentCloseoutStatus outcome={closeout} isLoading={closeoutLoading} isError={closeoutError} onRetry={retryCloseout} />
        <p className="text-ui text-muted-foreground">{registerText.thisProvisionalResultIsUnavailableNoPercentageLetterTotalOr}</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={refreshResult}>
            {registerText.check}</Button>
          <Button asChild variant="ghost">
            <a href={supportUrl ?? "mailto:dev@imam-us.org"}>{registerText.contactSupportAboutThisResult}</a>
          </Button>
        </div>
      </CourseRecord>
    );
  }

  if (read.status !== "current") {
    const title = read.status === "stale"
      ? registerText.thisProvisionalResultIsNoLongerCurrent
      : read.status === "inconsistent"
        ? registerText.thisProvisionalResultCannotBeShownSafely
        : registerText.thisProvisionalResultIsUnavailable;
    const supportUrl = read.support_url;
    return (
      <CourseRecord code={course.course_code} title={course.course_title} aria-live="polite">
        <StudentCloseoutStatus outcome={closeout} isLoading={closeoutLoading} isError={closeoutError} onRetry={retryCloseout} />
        <p className="text-ui text-muted-foreground">{title}{registerText.noPercentageLetterTotalOrItemContributionIsShown}</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={refreshResult}>
            {registerText.check}</Button>
          <Button asChild variant="ghost">
            <a href={supportUrl}>{registerText.contactSupportAboutThisResult}</a>
          </Button>
        </div>
      </CourseRecord>
    );
  }

  const { result } = read;
  // Each line names its related coursework, the exact work it opens (#1393
  // contract 2). A certified outcome is the record, so its lines link nothing;
  // while the closeout is unread, failed or absent, nothing links either.
  const related = !closeoutLoading && !closeoutError && closeout !== undefined
    && closeout.status !== "certified" && closeout.status !== "corrected";
  return (
    <CourseRecord
      code={result.course_code}
      title={result.course_title}
      lead={(
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-data text-tile-title font-semibold tabular-nums text-foreground">
            {result.percentage != null ? `${result.percentage}%` : registerText.notYetAvailable}
          </span>
          {!["-", "\u2014", ""].includes(result.letter) && (
            <span>
              {result.letter}
              {result.advisory ? registerText.advisory : ""}
            </span>
          )}
        </div>
      )}
    >
      <StudentCloseoutStatus
        outcome={closeout}
        isLoading={closeoutLoading}
        isError={closeoutError}
        onRetry={retryCloseout}
      />
      {/* Deliberately on the raw `ui/table` primitive rather than `RecordTable`.

          This is a derivation, not a record list: each row is one term in the
          weighted sum above it, and the table exists to show how the result was
          reached. A row has no identity to open and no state of its own, so the
          primary column the records layer builds around would point nowhere.

          The history list on this page is a `RecordTable`, and that is the
          record list on this page. If an assessment row ever gains its own
          detail surface, revisit this, not the comment. */}
      <TileTable scrollLabel={registerText.yourAssessmentBreakdown} stickyHeader>
        <TableHeader>
          <TableRow>
            <TableHead>{registerText.assessment}</TableHead>
            <TableHead>{registerText.weight2}</TableHead>
            <TableHead>{registerText.yourScore2}</TableHead>
            <TableHead className="text-right">{registerText.contribution}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {result.items.map((item) => {
            const score = item.score;
            const hasScore = score != null;
            const contribution = hasScore ? (score * item.weight) / 100 : null;
            return (
              <TableRow key={item.id}>
                <TableCell>
                  {item.label}
                  {related && item.related_work?.length ? (
                    <ul aria-label={registerPhrases.relatedWork(item.label)} className="mt-1 grid gap-1">
                      {item.related_work.map((work) => (
                        <li key={work.assignment_id}>
                          <Link dir="auto" className="text-ui text-primary underline-offset-4 hover:underline" to={`${courseWorkspacePath("student", result.course_id, "coursework")}&assignment=${encodeURIComponent(work.assignment_id)}`}>{work.title}</Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </TableCell>
                <TableCell className="font-data tabular-nums">{item.weight}%</TableCell>
                <TableCell className="font-data tabular-nums">{hasScore ? score : registerText.pending}</TableCell>
                <TableCell className="text-right font-data tabular-nums">{contribution != null ? registerPhrases.points(contribution) : registerText.notYetAvailable}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </TileTable>
    </CourseRecord>
  );
}

function StudentCloseoutStatus({
  outcome,
  isLoading,
  isError,
  onRetry,
}: {
  outcome?: StudentOutcome;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  if (isLoading) {
    return <div className="text-ui text-muted-foreground" role="status">{registerText.checkingCourseCloseoutStatus}</div>;
  }
  if (isError || !outcome) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-3">
        <p className="text-ui text-muted-foreground">{registerText.closeoutStatusIsUnavailableNoResultIsShownAsOfficial}</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>{registerText.retryCloseoutStatus}</Button>
      </div>
    );
  }

  if (outcome.status === "provisional" && !outcome.correction_history.length) return null;

  return (
    <section className="grid gap-1 border-t border-rule pt-3" aria-label={registerText.certificationStatus}>
      {/* The row's state already says Awaiting certification; this says only what it means (#1416). */}
      {outcome.status === "awaiting_certification" && (
        <p className="text-ui text-muted-foreground">{registerText.noResultIsOfficialUntilCertified}</p>
      )}
      {outcome.correction_history.length > 0 && (
        <span className="text-ui font-medium text-foreground">{registerText.corrections}</span>
      )}
      {outcome.correction_history.map((event) => (
        <div key={event.id} className="text-ui text-muted-foreground">
          {registerText.correction}{event.event_no}: {event.prior_letter ? registerPhrases.letterTo(event.prior_letter) : ""}{event.certified_letter}
          {event.reason ? ` · ${event.reason}` : ""}
        </div>
      ))}
    </section>
  );
}
